import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { L10N_LOCALE, L10nLocale, L10nTranslationModule } from 'angular-l10n';

export interface InputPromptData {
  title: string;
  message?: string;
  cancel?: string;
  cancelColor?: string; // 'primary' or 'warn'
  ok?: string;
  okColor?: string; // 'primary' or 'warn'
  requireInput?: boolean;
  inputPlaceholder?: string;
  warningText?: string;
  value?: string;
  /** Why the input can't be used (already translated), or null; checked as the user types */
  validate?: (value: string) => string | null;
}

@Component({
  selector: 'app-input-prompt',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    L10nTranslationModule,
  ],
  templateUrl: './input-prompt.component.html',
  styleUrl: './input-prompt.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class InputPromptComponent {
  readonly locale = inject<L10nLocale>(L10N_LOCALE);
  readonly dialogRef = inject<MatDialogRef<InputPromptComponent>>(MatDialogRef);
  readonly data = inject<InputPromptData>(MAT_DIALOG_DATA);

  okColor: string = 'primary';
  cancelColor: string = 'primary';

  inputValue: FormControl<string> = new FormControl(this.data?.value ?? '', {
    nonNullable: true,
    validators: this.promptValidator(),
  });
  readonly errorState: ErrorStateMatcher = { isErrorState: (control) => !!control?.invalid };

  get error(): string | null {
    return (this.inputValue.errors?.['prompt'] as string | undefined) ?? null;
  }

  get okDisabled(): boolean {
    if (this.inputValue.invalid) return true;
    if (!this.data.requireInput) return false;
    return !this.inputValue.value?.trim();
  }

  get okCloseValue(): string {
    return this.inputValue.value?.trim();
  }

  constructor() {
    const data = this.data;

    this.okColor = data?.okColor ?? this.okColor;
    this.cancelColor = data?.cancelColor ?? this.cancelColor;
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (!this.okDisabled) {
      this.dialogRef.close(this.okCloseValue);
    }
  }

  private promptValidator(): ValidatorFn | null {
    const validate = this.data?.validate;
    if (!validate) return null;
    return (control) => {
      const message = validate(control.value as string);
      return message === null ? null : { prompt: message };
    };
  }
}
